import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

import { HappyUsers } from '@/components/landing/HappyUsers';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import SpecialOfferLandingPageNavigation from '@/components/layout/SpecialOfferLandingPageNavigation';

import { useSpecialOffer } from '@/utils/useSpecialOffer';

import ModelLogosMarquee from './ModelLogosMarquee';

const MEDIA = 'https://media.easy-peasy.ai/landing/home';

type WallTile = {
  src: string;
  aspect: string;
  video?: boolean;
  poster?: string;
};

// Four vertical columns of generated media, Higgsfield-style. Each column is
// duplicated in the DOM so the translateY loop is seamless. The two columns
// visible on mobile are image-only — videos live in the sm+/lg+ columns so
// phones never stream hero video.
const WALL_COLUMNS: WallTile[][] = [
  [
    { src: `${MEDIA}/img-fashion.webp`, aspect: '3/4' },
    { src: `${MEDIA}/img-neon.webp`, aspect: '9/16' },
    { src: `${MEDIA}/audio-music.webp`, aspect: '4/3' },
    { src: `${MEDIA}/img-interior.webp`, aspect: '4/3' },
  ],
  [
    { src: `${MEDIA}/img-anime.webp`, aspect: '3/4' },
    { src: `${MEDIA}/img-hummingbird.webp`, aspect: '4/3' },
    { src: `${MEDIA}/img-headshot.webp`, aspect: '1/1' },
    { src: `${MEDIA}/more-templates.webp`, aspect: '4/3' },
  ],
  [
    {
      src: `${MEDIA}/vid-portrait.mp4`,
      poster: `${MEDIA}/vid-portrait-poster.jpg`,
      aspect: '9/16',
      video: true,
    },
    { src: `${MEDIA}/img-castle.webp`, aspect: '9/16' },
    { src: `${MEDIA}/audio-tts.webp`, aspect: '4/3' },
  ],
  [
    { src: `${MEDIA}/img-architecture.webp`, aspect: '3/4' },
    { src: `${MEDIA}/img-android.webp`, aspect: '1/1' },
    {
      src: `${MEDIA}/vid-coast.mp4`,
      poster: `${MEDIA}/vid-coast-poster.jpg`,
      aspect: '16/9',
      video: true,
    },
    { src: `${MEDIA}/more-customagents.webp`, aspect: '4/3' },
  ],
];

// Spans the whole platform, not just media: creative, productivity, agent
// and audio tools alternate so no two neighbors feel same-category.
const ROTATING_WORDS = [
  'automated workflows',
  'custom AI agents',
  'entire websites',
  'cinematic videos',
  'slide decks',
  'lifelike voices',
  'deep research',
  'talking avatars',
  'stunning images',
  'meeting notes',
  'music & songs',
  'mobile apps',
];

const WallColumn = ({
  tiles,
  duration,
  reverse,
  className = '',
  eager = false,
}: {
  tiles: WallTile[];
  duration: number;
  reverse?: boolean;
  className?: string;
  // Only the columns that are visible at the smallest breakpoint preload, and
  // only their first tiles: the second copy exists purely to make the scroll
  // loop seamless, and columns 3-4 are display:none below sm/lg — eagerly
  // loading either just delays the hero's real LCP.
  eager?: boolean;
}) => (
  <div className={`relative flex-1 overflow-hidden ${className}`}>
    <div
      className='flex flex-col gap-3 motion-reduce:!animate-none'
      style={{
        animation: `hero-wall-scroll ${duration}s linear infinite ${
          reverse ? 'reverse' : ''
        }`,
      }}
    >
      {[0, 1].map((copy) => (
        <div
          key={copy}
          className='flex flex-col gap-3'
          aria-hidden={copy === 1 || undefined}
        >
          {tiles.map((tile, tileIndex) => (
            <div
              key={`${copy}-${tile.src}`}
              className='relative w-full overflow-hidden rounded-xl bg-white/5'
              style={{ aspectRatio: tile.aspect }}
            >
              {tile.video ? (
                <video
                  className='absolute inset-0 h-full w-full object-cover'
                  src={tile.src}
                  poster={tile.poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload='none'
                />
              ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={tile.src}
                  alt=''
                  loading={
                    eager && copy === 0 && tileIndex < 2 ? 'eager' : 'lazy'
                  }
                  decoding='async'
                  className='absolute inset-0 h-full w-full object-cover'
                />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const HomeHero = () => {
  const { t } = useLocale('home');
  const { isSpecialOffer } = useSpecialOffer();
  const [wordIndex, setWordIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordVisible(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % ROTATING_WORDS.length);
        setWordVisible(true);
      }, 250);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className='relative overflow-hidden bg-[#050509]'>
      {/* Navigation */}
      <div className='absolute top-0 left-0 w-full z-30'>
        {isSpecialOffer ? (
          <SpecialOfferLandingPageNavigation />
        ) : (
          <LandingPageNavigation variant='light' skipUserContext forceDark />
        )}
      </div>

      {/* Media wall background */}
      <div className='absolute inset-0 z-0 flex gap-3 px-3 opacity-60 md:opacity-70'>
        <WallColumn tiles={WALL_COLUMNS[0]} duration={90} eager />
        <WallColumn tiles={WALL_COLUMNS[1]} duration={70} reverse eager />
        <WallColumn
          tiles={WALL_COLUMNS[2]}
          duration={100}
          className='hidden sm:block'
        />
        <WallColumn
          tiles={WALL_COLUMNS[3]}
          duration={80}
          reverse
          className='hidden lg:block'
        />
      </div>

      {/* Legibility overlays */}
      <div className='pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_60%_55%_at_50%_45%,rgba(5,5,9,0.92),rgba(5,5,9,0.55)_65%,rgba(5,5,9,0.25))]' />
      <div className='pointer-events-none absolute inset-x-0 top-0 z-10 h-32 bg-gradient-to-b from-[#050509] to-transparent' />
      <div className='pointer-events-none absolute inset-x-0 bottom-0 z-10 h-40 bg-gradient-to-t from-[#050509] to-transparent' />

      {/* Content */}
      <div className='relative z-20 mx-auto flex min-h-[100svh] max-w-5xl flex-col items-center justify-center px-4 pt-32 pb-24 text-center sm:px-6'>
        {isSpecialOffer ? (
          <div className='mb-8 inline-flex items-center rounded-full border border-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-sm font-medium text-indigo-200 backdrop-blur-sm'>
            <span className='mr-2 rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] font-bold text-white'>
              NEW
            </span>
            Get 30% off. Use code:{' '}
            <b className='ml-1 text-white'>TRYEASYPEASYAI</b>
          </div>
        ) : (
          <div className='mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-300 backdrop-blur-md sm:text-sm'>
            <span className='relative flex h-2 w-2'>
              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:hidden' />
              <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-400' />
            </span>
            {t('One AI platform. Every task.')}
          </div>
        )}

        <h1 className='text-4xl font-extrabold tracking-tight text-white drop-shadow-2xl sm:text-5xl md:text-6xl lg:text-7xl'>
          <span className='block'>{t('Ask anything,')}</span>
          <span className='block'>{t('create anything.')}</span>
          <span className='sr-only'>
            {' '}
            {t(
              'Images, videos, websites, slide decks, voices, research, notes, music, blog posts, apps, workflows and AI agents.',
            )}
          </span>
          {/* Gradient must live on the transformed span itself — background-clip:
              text does not paint through a transformed descendant in Chrome. */}
          <span aria-hidden='true' className='mt-1 block'>
            <span
              className={`inline-block bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text pb-2 text-transparent transition-all duration-300 ${
                wordVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-3 opacity-0'
              }`}
            >
              {t(ROTATING_WORDS[wordIndex])}
            </span>
          </span>
        </h1>

        <p className='mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-slate-300 md:text-xl'>
          {t(
            'AI For Every Task You Have. Work smarter, create faster, achieve more.',
          )}{' '}
          <span className='whitespace-nowrap font-semibold text-white'>
            {t('Easy peasy.')}
          </span>
        </p>

        <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
          <Link
            href='/login#auth-sign-up'
            className='group inline-flex h-[58px] min-w-[230px] items-center justify-center gap-2 rounded-full bg-indigo-500 px-8 text-lg font-bold text-white shadow-[0_8px_40px_-8px_rgba(99,102,241,0.7)] transition-all hover:bg-indigo-400 hover:shadow-[0_8px_50px_-6px_rgba(99,102,241,0.9)]'
          >
            {t('Start creating free')}
            <span
              aria-hidden='true'
              className='transition-transform group-hover:translate-x-1'
            >
              →
            </span>
          </Link>
          <a
            href='#showcase'
            className='inline-flex h-[58px] min-w-[180px] items-center justify-center rounded-full border border-white/20 bg-white/5 px-8 text-lg font-semibold text-white backdrop-blur-md transition-all hover:border-white/40 hover:bg-white/10'
          >
            {t('See what it makes')}
          </a>
        </div>

        <div className='mt-8 flex justify-center'>
          <HappyUsers fromLandingPage={true} className='items-center' />
        </div>
      </div>

      {/* Model logos strip pinned to hero bottom */}
      <div className='relative z-20 pb-6'>
        <ModelLogosMarquee />
      </div>

      <style jsx global>{`
        @keyframes hero-wall-scroll {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(calc(-50% - 6px));
          }
        }
      `}</style>
    </section>
  );
};

export default HomeHero;
